import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'

// The date range the Company tab is showing. Widgets that depend on it (money, top clients) read it
// here and send it to the API; the picker above the grid changes it. The choice is kept per browser.

export type PeriodKey = 'today' | 'week' | 'month' | 'last_month' | 'fy' | 'custom'

export interface PeriodChoice {
  period: PeriodKey
  /** YYYY-MM-DD, for `custom`. */
  from?: string
  to?: string
}

export const PERIOD_PRESETS: Array<{ key: Exclude<PeriodKey, 'custom'>; label: string }> = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This week' },
  { key: 'month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'fy', label: 'This FY' },
]

export const DEFAULT_PERIOD: PeriodChoice = { period: 'month' }
const STORAGE_KEY = 'zynx.dashboard.period'
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

/** Query parameters for the API. A custom range without both valid dates is sent as the default. */
export function periodParams(choice: PeriodChoice): Record<string, string> {
  if (choice.period === 'custom') {
    if (choice.from && choice.to && DATE_RE.test(choice.from) && DATE_RE.test(choice.to) && choice.from <= choice.to) {
      return { period: 'custom', from: choice.from, to: choice.to }
    }
    return { period: DEFAULT_PERIOD.period }
  }
  return { period: choice.period }
}

function readStored(): PeriodChoice {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULT_PERIOD
    const parsed = JSON.parse(raw) as PeriodChoice
    const known = parsed.period === 'custom' || PERIOD_PRESETS.some((p) => p.key === parsed.period)
    return known ? parsed : DEFAULT_PERIOD
  } catch {
    return DEFAULT_PERIOD
  }
}

interface PeriodContextValue {
  choice: PeriodChoice
  setChoice: (choice: PeriodChoice) => void
}

const PeriodContext = createContext<PeriodContextValue>({ choice: DEFAULT_PERIOD, setChoice: () => {} })

export function DashboardPeriodProvider({ children }: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<PeriodChoice>(readStored)
  const setChoice = useCallback((next: PeriodChoice) => {
    setChoiceState(next)
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
    } catch {
      // Private window or blocked storage: the choice just lasts for this visit.
    }
  }, [])
  const value = useMemo(() => ({ choice, setChoice }), [choice, setChoice])
  return <PeriodContext.Provider value={value}>{children}</PeriodContext.Provider>
}

export function useDashboardPeriod() {
  return useContext(PeriodContext)
}
